import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Edit,
  Eye,
  Shield,
  Search,
  AlertCircle,
  Save,
  X,
  ArrowLeft,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_staff_rights,
  action_staff_right,
  save_update_staff_rights,
} from "../ServiceConnection/serviceconnection";
import {
  handleAlphabetRightId,
  handleURLChange_Modify,
} from "../CommonJquery/CommonJquery";

export default function SubStaffRights() {
  const navigate = useNavigate();
  const { id: parentId } = useParams();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [parentRight, setParentRight] = useState(null);
  const [panelName, setPanelName] = useState(null);
  const [subRights, setSubRights] = useState([]);
  const [filteredRights, setFilteredRights] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [selectedRight, setSelectedRight] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    rightName: "",
    url: "",
    landingPage: "",
  });
  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState("0");

  // Fetch parent right
  const fetchParentRight = useCallback(async () => {
    if (!parentId) return;

    const fd = new FormData();
    fd.append("call_id", parentId);
    fd.append("flag", "3");

    try {
      const response = await server_post_data(get_all_staff_rights, fd);

      if (!response.data.error && response.data.message.data.length > 0) {
        setParentRight(response.data.message.data[0]);
        setPanelName(response.data.message.data[0].panel_name);
      }
    } catch (error) {
      console.error("Error fetching parent right:", error);
      toast({
        title: "Error",
        description: "Failed to load parent permission",
        variant: "destructive",
      });
    }
  }, [parentId, toast]);

  // Fetch sub rights
  const fetchSubRights = useCallback(async () => {
    if (!parentId) return;

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", parentId);
    fd.append("flag", "2");

    try {
      const response = await server_post_data(get_all_staff_rights, fd);

      if (!response.data.error) {
        const rightsData = response.data.message.data || [];
        setSubRights(rightsData);
        setFilteredRights(rightsData);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching sub-rights:", error);
      toast({
        title: "Error",
        description: "Failed to load sub-permissions",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [parentId, toast]);

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);

      if (!query.trim()) {
        setFilteredRights(subRights);
        return;
      }

      const searchLower = query.toLowerCase();
      const results = subRights.filter(
        (right) =>
          right.page_name?.toLowerCase().includes(searchLower) ||
          right.page_url_id?.toLowerCase().includes(searchLower)
      );

      setFilteredRights(results);
    },
    [subRights]
  );

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Open create dialog
  const handleOpenCreateDialog = useCallback(() => {
    setEditMode(false);
    setEditId("0");
    setFormData({
      rightName: "",
      url: "",
      landingPage: "",
    });
    setErrors({});
    setIsCreateDialogOpen(true);
  }, []);

  // Open edit dialog
  const handleOpenEditDialog = useCallback(
    async (right) => {
      setEditMode(true);
      setEditId(right.id);

      const fd = new FormData();
      fd.append("call_id", right.id);
      fd.append("flag", "4");

      try {
        const response = await server_post_data(get_all_staff_rights, fd);

        if (!response.data.error && response.data.message.data.length > 0) {
          const rightData = response.data.message.data[0];
          setFormData({
            rightName: rightData.page_name || "",
            url: rightData.page_url_id || "",
            landingPage: rightData.landing_page || "",
          });
          setIsCreateDialogOpen(true);
        }
      } catch (error) {
        console.error("Error fetching right data:", error);
        toast({
          title: "Error",
          description: "Failed to load sub-permission data",
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.rightName.trim()) {
      newErrors.rightName = "Permission name is required";
    } else if (formData.rightName.trim().length < 3) {
      newErrors.rightName = "Permission name must be at least 3 characters";
    }

    if (!formData.url.trim()) {
      newErrors.url = "URL is required";
    } else if (formData.url.trim().length < 3) {
      newErrors.url = "URL must be at least 3 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("main_id", editId);
    fd.append("parent_id", parentId);
    fd.append("right_name", formData.rightName.trim());
    fd.append("class_name", formData.url.trim());
    fd.append("landing_page", panelName);

    try {
      const response = await server_post_data(save_update_staff_rights, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editMode
            ? "Sub-permission updated successfully"
            : "Sub-permission created successfully",
        });
        setIsCreateDialogOpen(false);
        fetchSubRights();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save sub-permission",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    formData,
    editId,
    parentId,
    editMode,
    validateForm,
    toast,
    fetchSubRights,
  ]);

  // Handle toggle status
  const handleToggleClick = useCallback((right) => {
    setSelectedRight(right);
    setIsToggleDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!selectedRight) return;

    setShowLoaderAdmin(true);

    const newStatus = selectedRight.flag === "c" ? "d" : "c";

    const fd = new FormData();
    fd.append("id_for_delete", selectedRight.id);
    fd.append("flag_for", "1");
    fd.append("for_status_final", newStatus);

    try {
      const response = await server_post_data(action_staff_right, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Status updated successfully",
        });
        fetchSubRights();
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsToggleDialogOpen(false);
      setSelectedRight(null);
    }
  }, [selectedRight, toast, fetchSubRights]);

  // Initial load
  useEffect(() => {
    fetchParentRight();
    fetchSubRights();
  }, [fetchParentRight, fetchSubRights]);

  const stats = {
    total: subRights.length,
    active: subRights.filter((r) => r.flag === "c").length,
    inactive: subRights.filter((r) => r.flag === "d").length,
  };

  return (
    <MainLayout>
      <Header
        title="Sub-Permissions Management"
        subtitle={
          parentRight
            ? `Manage sub-permissions for "${parentRight.page_name}"`
            : "Manage sub-permissions"
        }
      />

      <div className="p-6 space-y-6">
        {/* Parent Context */}
        {parentRight && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Parent Permission
                  </p>
                  <p className="font-semibold text-foreground">
                    {parentRight.page_name}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/view-permissions")}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.total}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Sub-Permissions
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.active}
                </p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.inactive}
                </p>
                <p className="text-sm text-muted-foreground">Inactive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or URL..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setFilteredRights(subRights);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Button
            onClick={handleOpenCreateDialog}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Sub-Permission
          </Button>
        </div>

        {/* Sub-Rights Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">
                  Permission Name
                </TableHead>
                <TableHead className="text-muted-foreground">URL ID</TableHead>
                <TableHead className="text-muted-foreground">
                  Panel Name
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRights.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-muted-foreground"
                  >
                    No sub-permissions found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRights.map((right) => (
                  <TableRow key={right.id} className="border-border">
                    <TableCell className="font-semibold">
                      {right.page_name}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {right.page_url_id}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {right.panel_name}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={right.flag === "c"}
                        onCheckedChange={() => handleToggleClick(right)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEditDialog(right)}
                          className="hover:bg-primary/10 hover:text-primary"
                          title="Edit Sub-Permission"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(`/inside-pagerights/${right.id}`)
                          }
                          className="hover:bg-primary/10 hover:text-primary"
                          title="Inside Page Rights"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="ml-1 text-xs">Inside</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editMode ? "Edit Sub-Permission" : "Create New Sub-Permission"}
              </DialogTitle>
              <DialogDescription>
                {editMode
                  ? "Update sub-permission details"
                  : "Add a new sub-permission to the system"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Permission Name */}
              <div className="space-y-2">
                <Label>
                  Permission Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g., View Reports, Export Data"
                  value={formData.rightName}
                  onChange={(e) =>
                    handleFieldChange("rightName", e.target.value)
                  }
                  className={errors.rightName ? "border-destructive" : ""}
                  minLength={3}
                  maxLength={75}
                />
                {errors.rightName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.rightName}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* URL */}
                <div className="space-y-2">
                  <Label>
                    URL <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="view-reports"
                    value={formData.url}
                    onChange={(e) => {
                      handleURLChange_Modify(e);
                      handleFieldChange("url", e.target.value);
                    }}
                    className={errors.url ? "border-destructive" : ""}
                    minLength={3}
                    maxLength={75}
                  />
                  {errors.url && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.url}
                    </p>
                  )}
                </div>

                {/* Landing Page */}
                <div className="space-y-2">
                  <Label>Panel</Label>
                  <Select
                    value={panelName}
                    onValueChange={(value) =>
                      handleFieldChange("landingPage", value)
                    }
                    disabled="true"
                  >
                    <SelectTrigger
                      className={errors.landingPage ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select Panel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="POS">POS</SelectItem>
                      <SelectItem value="Dashboard">Dashboard</SelectItem>
                      <SelectItem value="Mobile Pos">Mobile Pos</SelectItem>
                      <SelectItem value="Manager App">Manager App</SelectItem>
                      <SelectItem value="Captain App">Captain App</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={showLoaderAdmin}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={showLoaderAdmin}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? (
                  "Saving..."
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editMode ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Toggle Status Dialog */}
        <AlertDialog
          open={isToggleDialogOpen}
          onOpenChange={setIsToggleDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change Sub-Permission Status</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to{" "}
                {selectedRight?.flag === "c" ? "deactivate" : "activate"} the
                sub-permission <strong>{selectedRight?.page_name}</strong>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmToggle}>
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
